
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, BookOpen, FileText, Code, Trophy, Search } from "lucide-react";
import { DocsBlogSection } from "@/components/docs/DocsBlogSection";
import { DocsStudiesSection } from "@/components/docs/DocsStudiesSection";
import { DocsTechDocsSection } from "@/components/docs/DocsTechDocsSection";
import { DocsAPISection } from "@/components/docs/DocsAPISection";

const Documentation = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedTab, setSelectedTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Searching documentation",
      description: `Searching for: ${searchQuery}`,
    });
    // Optional: Integrate actual search here
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      <Header />

      <section className="pt-24 px-4 sm:px-6 lg:px-8 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white"
              onClick={() => navigate("/")}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-violet-500">
                Stewie Documentation
              </span>
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Explore our comprehensive resources, technical papers, and insights
              to maximize your trading potential with Stewie AI.
            </p>
          </div>
          <div className="max-w-2xl mx-auto mb-12">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Search documentation..."
                className="w-full pl-10 pr-12 py-3 bg-gray-800/50 text-white border-gray-700 rounded-xl focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Button 
                type="submit"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg bg-blue-600 hover:bg-blue-700"
              >
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Tabs 
          defaultValue="overview"
          className="w-full"
          onValueChange={setSelectedTab}
        >
          <div className="flex justify-center mb-8">
            <ScrollArea className="w-full max-w-3xl">
              <TabsList className="inline-flex p-1 bg-gray-800/50 rounded-xl">
                <TabsTrigger 
                  value="overview" 
                  className="data-[state=active]:bg-blue-600 rounded-lg text-white"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Blog
                </TabsTrigger>
                <TabsTrigger 
                  value="techDocs" 
                  className="data-[state=active]:bg-blue-600 rounded-lg text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Docs
                </TabsTrigger>
                <TabsTrigger 
                  value="studies" 
                  className="data-[state=active]:bg-blue-600 rounded-lg text-white"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Studies
                </TabsTrigger>
                <TabsTrigger 
                  value="api" 
                  className="data-[state=active]:bg-blue-600 rounded-lg text-white"
                >
                  <Code className="w-4 h-4 mr-2" />
                  API
                </TabsTrigger>
              </TabsList>
            </ScrollArea>
          </div>
          <TabsContent value="overview">
            <DocsBlogSection />
          </TabsContent>
          <TabsContent value="techDocs">
            <DocsTechDocsSection />
          </TabsContent>
          <TabsContent value="studies">
            <DocsStudiesSection />
          </TabsContent>
          <TabsContent value="api">
            <DocsAPISection />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-1/4 right-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-violet-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>
    </div>
  );
};

export default Documentation;
